/**
 * Reading salary PIN outcomes off what a ts-rest call hands a screen (ADR 0041).
 *
 * A non-2xx ts-rest response reaches `onError` (and a query's `error`) as `{ status, body, headers }`, so
 * these accept that shape and the bare body alike.
 */

import { isSalaryPinRequiredBody, type SalaryPinRequiredDetails } from '@hrms/shared';

function bodyOf(error: unknown): unknown {
  return (error as { body?: unknown } | null | undefined)?.body ?? error;
}

/** The API refused because pay is locked behind the salary PIN — show "locked", not "no access". */
export function isSalaryPinRequiredError(
  error: unknown,
): error is { status: 403; body: { details: SalaryPinRequiredDetails } } {
  return isSalaryPinRequiredBody(bodyOf(error));
}

export function salaryPinRequiredDetails(error: unknown): SalaryPinRequiredDetails | null {
  const body = bodyOf(error);
  return isSalaryPinRequiredBody(body) ? body.details : null;
}

/** A wrong PIN: `attemptsRemaining`, or null for any other error. */
export function wrongPinAttemptsRemaining(error: unknown): number | null {
  const body = bodyOf(error) as { code?: unknown; details?: { attemptsRemaining?: unknown } } | null;
  if (body?.code !== 'SALARY_PIN_INCORRECT') return null;
  const remaining = body.details?.attemptsRemaining;
  return typeof remaining === 'number' ? remaining : null;
}

/** The sentence the API sent, or a fallback. */
export function salaryPinErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error) return "Can't reach the server. Check your connection and try again.";
  const message = (bodyOf(error) as { message?: unknown } | null)?.message;
  return typeof message === 'string' && message.trim() ? message : fallback;
}

/** `details.reason` of a refusal (`phone_unverified` on a reset whose SMS proof was refused). */
export function errorReason(error: unknown): unknown {
  return (bodyOf(error) as { details?: { reason?: unknown } } | null)?.details?.reason;
}

/**
 * One response header off cached ts-rest data. ts-rest hands a `Headers`; persisted or test data may carry
 * a plain record. An infinite query's data holds its responses in `pages`.
 */
export function headerOf(data: unknown, name: string): string | null {
  if (!data || typeof data !== 'object') return null;
  const pages = (data as { pages?: unknown }).pages;
  if (Array.isArray(pages)) {
    for (const page of pages) {
      const value = headerOf(page, name);
      if (value !== null) return value;
    }
    return null;
  }
  const headers = (data as { headers?: unknown }).headers;
  if (!headers || typeof headers !== 'object') return null;
  if (typeof (headers as Headers).get === 'function') return (headers as Headers).get(name);
  const wanted = name.toLowerCase();
  for (const [key, value] of Object.entries(headers as Record<string, unknown>)) {
    if (key.toLowerCase() === wanted && typeof value === 'string') return value;
  }
  return null;
}

/** The API marks every response it served from behind the salary PIN (`X-Salary-Pin: gated`). */
export function isSalaryGatedData(data: unknown): boolean {
  return headerOf(data, 'x-salary-pin') === 'gated';
}
